import { useState } from 'react'
import { useEnhancedBrandStore } from '../../stores/enhancedBrandStore'
import { ChevronDownIcon, ChevronUpIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { InteractiveAmazonChart } from './interactive/InteractiveAmazonChart'
import { InteractiveManufacturingChart } from './interactive/InteractiveManufacturingChart'
import { InteractiveCategoriesChart } from './interactive/InteractiveCategoriesChart'
import { InteractivePriceChart } from './interactive/InteractivePriceChart'
import { AdvancedFiltersSection } from './interactive/AdvancedFiltersSection'

export function InteractiveAnalyticsFilters() {
  const { clearAllFilters } = useEnhancedBrandStore()
  const [expandedSection, setExpandedSection] = useState<string | null>(null)

  const toggleSection = (sectionId: string) => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId)
  }

  const sections = [
    {
      id: 'amazon',
      title: 'Amazon Presence',
      icon: '📦',
      component: <InteractiveAmazonChart />
    },
    {
      id: 'manufacturing',
      title: 'Manufacturing',
      icon: '🏭',
      component: <InteractiveManufacturingChart />
    },
    {
      id: 'categories',
      title: 'Categories',
      icon: '🏷️',
      component: <InteractiveCategoriesChart />
    },
    {
      id: 'price',
      title: 'Price Tiers',
      icon: '💰',
      component: <InteractivePriceChart />
    },
    {
      id: 'advanced',
      title: 'Advanced',
      icon: '⚙️',
      component: <AdvancedFiltersSection />
    }
  ]

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 layout-transition">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Interactive Analytics & Filters
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Click charts to filter data • Explore and refine your view
            </p>
          </div>
          <button
            onClick={clearAllFilters}
            className="flex items-center space-x-2 px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 border border-red-600 dark:border-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
          >
            <XMarkIcon className="h-4 w-4" />
            <span>Clear All</span>
          </button>
        </div>
      </div>

      {/* Interactive Sections */}
      <div className="divide-y divide-gray-200 dark:divide-gray-700">
        {sections.map((section) => (
          <div key={section.id}>
            {/* Section Header */}
            <button
              onClick={() => toggleSection(section.id)}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              <div className="flex items-center space-x-3">
                <span className="text-xl">{section.icon}</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {section.title}
                </span>
              </div>
              {expandedSection === section.id ? (
                <ChevronUpIcon className="h-5 w-5 text-gray-400" />
              ) : (
                <ChevronDownIcon className="h-5 w-5 text-gray-400" />
              )}
            </button>

            {/* Section Content */}
            {expandedSection === section.id && (
              <div className="px-6 pt-4 pb-6">
                {section.component}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}